/**
 * 媒体账户页面 — OwnedAccounts（二期批3 翻新，对齐概念稿 owned-accounts.html）。
 *
 * GET   /api/owned-accounts?level&company_id&brand_id&product_id  列表（选择器三级过滤 + R3 产品并集）
 * GET   /api/products?brand_id|company_id=                        设置专属弹窗的产品候选
 * PATCH /api/owned-accounts/{id}                                  设置/解除专属（二期批3 新端点）
 *
 * 结构：4 指标卡 → 工具栏（R3 归属桶 + 层级筛选）→ 账户表（R3 徽标 + 专属操作）。
 * 核心新增（PRD §4.6.2 R3「资源一条记录、可选专属」）：
 * - 归属徽标：product_id 为空 = 「全公司共享」，非空 = 「专属 · {产品名}」；
 * - 选择器选中产品时后端返回 共享 + 专属此产品 并集，徽标区分；
 * - 行内设置/解除专属：弹窗选产品调 PATCH，解除则恢复全公司共享（R4：删产品不删资源）。
 *
 * 概念稿裁剪：粉丝量/本月发文/收录率/权重档位/分平台收录对比/账户健康提示
 * 无接口字段（属收录台账域），按"接口没有不虚构"原则跳过。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { LaunchOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getOwnedAccounts, patchOwnedAccount } from "../api/ownedAccounts";
import { getProducts } from "../api/products";
import { useCompanyStore } from "../store/companyStore";
import { AUTH_STATUS_MAP } from "../utils/constants";
import { formatDate } from "../utils/format";
import type { PlatformAccountItem, ProductListItem } from "../types";

/** R3 归属桶。 */
type Bucket = "all" | "shared" | "dedicated";

/** 可点击过滤桶徽标（选中=实色，未选中=灰底）。 */
function BucketChip({
  label,
  count,
  active,
  color,
  bg,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <Chip
      label={`${label} ${count}`}
      size="small"
      onClick={onClick}
      sx={{
        fontSize: 11,
        height: 22,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? color : "text.secondary",
        bgcolor: active ? bg : "semanticBg.neutral",
        border: "1px solid",
        borderColor: active ? color : "transparent",
      }}
    />
  );
}

/** 层级中文映射。 */
const LEVEL_MAP: Record<string, string> = {
  company: "公司级",
  brand: "品牌级",
  product: "产品级",
};

export default function OwnedAccounts() {
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  const [allItems, setAllItems] = useState<PlatformAccountItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [bucket, setBucket] = useState<Bucket>("all");

  // 设置专属弹窗
  const [tagRecord, setTagRecord] = useState<PlatformAccountItem | null>(null);
  const [tagProducts, setTagProducts] = useState<ProductListItem[]>([]);
  const [tagProductId, setTagProductId] = useState("");
  const [tagLoading, setTagLoading] = useState(false);
  const [tagSubmitting, setTagSubmitting] = useState(false);
  const [tagError, setTagError] = useState("");

  // 解除专属提交中状态（按行 id）
  const [clearingId, setClearingId] = useState("");

  // 成功提示
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  /** 按选择器三级 + R3 产品并集拉取。 */
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getOwnedAccounts({
        level: levelFilter || undefined,
        company_id: selectedCompanyId || undefined,
        brand_id: selectedBrandId || undefined,
        // R3：选中产品时后端返回「全公司共享 + 专属此产品」并集
        product_id: selectedProductId || undefined,
      });
      setAllItems(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取媒体账户失败");
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [levelFilter, selectedCompanyId, selectedBrandId, selectedProductId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  /** 聚合指标。 */
  const stats = useMemo(() => {
    const platforms = new Set(allItems.map((r) => r.platform));
    const verified = allItems.filter((r) => r.auth_status === "verified").length;
    const shared = allItems.filter((r) => !r.product_id).length;
    return {
      total: allItems.length,
      platforms: platforms.size,
      verified,
      shared,
      dedicated: allItems.length - shared,
    };
  }, [allItems]);

  /** 归属桶过滤后的展示集。 */
  const items = useMemo(() => {
    if (bucket === "shared") return allItems.filter((r) => !r.product_id);
    if (bucket === "dedicated") return allItems.filter((r) => !!r.product_id);
    return allItems;
  }, [allItems, bucket]);

  /** 打开设置专属弹窗：加载该账户品牌（或公司）下的产品候选。 */
  const handleOpenTag = async (record: PlatformAccountItem) => {
    setTagRecord(record);
    // 预选：已是专属回显原产品；否则跟随选择器产品
    setTagProductId(record.product_id || selectedProductId || "");
    setTagError("");
    setTagLoading(true);
    try {
      const res = record.brand_id
        ? await getProducts({ brand_id: record.brand_id, size: 100 })
        : record.company_id
          ? await getProducts({ company_id: record.company_id, size: 100 })
          : await getProducts({ size: 100 });
      setTagProducts(res.items || []);
    } catch {
      setTagProducts([]);
      setTagError("产品候选加载失败，请稍后重试");
    } finally {
      setTagLoading(false);
    }
  };

  /** 提交设置专属。 */
  const handleSubmitTag = async () => {
    if (!tagRecord || !tagProductId) return;
    setTagSubmitting(true);
    setTagError("");
    try {
      const updated = await patchOwnedAccount(tagRecord.id, tagProductId);
      setAllItems((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
      );
      setSnackMsg(
        `已设置专属：${updated.platform}（${updated.auth_entity || "未认证主体"}）→ ${updated.product_name || "产品"}`,
      );
      setSnackOpen(true);
      setTagRecord(null);
    } catch (err) {
      setTagError(err instanceof Error ? err.message : "设置专属失败，请稍后重试");
    } finally {
      setTagSubmitting(false);
    }
  };

  /** 解除专属（恢复全公司共享）。 */
  const handleClearDedicated = async (record: PlatformAccountItem) => {
    setClearingId(record.id);
    setError("");
    try {
      const updated = await patchOwnedAccount(record.id, null);
      setAllItems((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
      );
      setSnackMsg(`已解除专属：${updated.platform} 恢复全公司共享`);
      setSnackOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "解除专属失败，请稍后重试");
    } finally {
      setClearingId("");
    }
  };

  const columns: DataTableColumn<PlatformAccountItem>[] = [
    {
      key: "platform",
      label: "平台",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {row.platform}
          </Typography>
          {row.account_type && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {row.account_type}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "auth_entity",
      label: "认证主体",
      render: (row) => (
        <Typography sx={{ fontSize: 12 }}>
          {row.auth_entity || "—"}
        </Typography>
      ),
    },
    {
      key: "level",
      label: "层级",
      align: "center",
      render: (row) => (
        <Badge label={LEVEL_MAP[row.level] || row.level} variant="info" />
      ),
    },
    {
      key: "ownership",
      label: "归属（R3）",
      align: "center",
      render: (row) =>
        row.product_id ? (
          <Badge
            label={`专属 · ${row.product_name || "（已删除产品）"}`}
            color="primary.main"
            bgColor="brand.50"
          />
        ) : (
          <Badge label="全公司共享" />
        ),
    },
    {
      key: "auth_status",
      label: "认证状态",
      align: "center",
      render: (row) => {
        const info = AUTH_STATUS_MAP[row.auth_status || ""] || {
          label: row.auth_status || "—",
          color: "text.secondary",
          bg: "background.default",
        };
        return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
      },
    },
    {
      key: "expiry",
      label: "到期日",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDate(row.auth_expiry)}
        </Typography>
      ),
    },
    {
      key: "url",
      label: "主页",
      align: "center",
      render: (row) =>
        row.homepage_url ? (
          <Button
            size="small"
            variant="text"
            endIcon={<LaunchOutlined sx={{ fontSize: 12 }} />}
            href={row.homepage_url}
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            访问
          </Button>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleOpenTag(row)}
            sx={{ fontSize: 12 }}
          >
            {row.product_id ? "更改专属" : "设置专属"}
          </Button>
          {row.product_id && (
            <Button
              size="small"
              variant="text"
              color="warning"
              disabled={clearingId === row.id}
              onClick={() => handleClearDedicated(row)}
              sx={{ fontSize: 12 }}
            >
              {clearingId === row.id ? "解除中..." : "解除专属"}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="媒体账户"
        breadcrumbs={[{ label: "资产管理" }, { label: "媒体账户" }]}
        subtitle="自有媒体矩阵 · 资源池一条记录全公司共享，可按需专属某产品（R3）"
        actions={
          selectedProductId ? (
            <Badge
              label="选择器已选产品：显示共享 + 专属此产品"
              variant="info"
              size="medium"
            />
          ) : undefined
        }
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="媒体账户总数"
            value={loading ? "—" : stats.total}
            subtitle="当前选择器范围"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="覆盖平台"
            value={loading ? "—" : stats.platforms}
            subtitle="按平台去重"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="已认证"
            value={loading ? "—" : stats.verified}
            subtitle={
              stats.total > 0
                ? `认证率 ${((stats.verified / stats.total) * 100).toFixed(0)}%`
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="全公司共享"
            value={loading ? "—" : stats.shared}
            subtitle={`产品专属 ${stats.dedicated} 个 · 共享/专属徽标见列表`}
          />
        </Grid>
      </Grid>

      {/* 工具栏：R3 归属桶 + 层级筛选 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <BucketChip
              label="全部"
              count={allItems.length}
              active={bucket === "all"}
              color="primary.main"
              bg="brand.50"
              onClick={() => setBucket("all")}
            />
            <BucketChip
              label="全公司共享"
              count={stats.shared}
              active={bucket === "shared"}
              color="text.secondary"
              bg="semanticBg.neutral"
              onClick={() => setBucket("shared")}
            />
            <BucketChip
              label={selectedProductId ? "专属此产品" : "产品专属"}
              count={stats.dedicated}
              active={bucket === "dedicated"}
              color="info.main"
              bg="semanticBg.info"
              onClick={() => setBucket("dedicated")}
            />
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              select
              size="small"
              label="层级筛选"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">全部层级</MenuItem>
              <MenuItem value="company">公司级</MenuItem>
              <MenuItem value="brand">品牌级</MenuItem>
              <MenuItem value="product">产品级</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* 账户表 */}
      {loading ? (
        <Loading fullPage />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          showPagination={false}
          getRowId={(row) => row.id}
          emptyText={
            bucket === "dedicated"
              ? "暂无产品专属账户 · 可通过「设置专属」指定"
              : "暂无媒体账户"
          }
        />
      )}

      {/* 口径说明 */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
            资源池口径（PRD §4.6.2 R3/R4）：账户物理上全公司一条记录，默认
            「全公司共享」；「设置专属」仅写入 product_id 关联，多产品共用不重复录入；
            选择器选中产品时列表 = 共享 + 专属此产品；删除产品仅解除关联，账户保留。
            发文与收录表现属收录台账域，见「收录台账」页。
          </Typography>
        </CardContent>
      </Card>

      {/* 设置专属弹窗 */}
      <Dialog
        open={tagRecord !== null}
        onClose={() => !tagSubmitting && setTagRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          {tagRecord?.product_id ? "更改产品专属" : "设置产品专属"}
        </DialogTitle>
        <DialogContent>
          {tagRecord && (
            <>
              <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                {tagRecord.platform}
                {tagRecord.account_type ? ` · ${tagRecord.account_type}` : ""}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                {tagRecord.auth_entity || "未认证主体"} ·{" "}
                {LEVEL_MAP[tagRecord.level] || tagRecord.level}
                {tagRecord.product_id
                  ? ` · 当前专属「${tagRecord.product_name || "（已删除产品）"}」`
                  : " · 当前全公司共享"}
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                label="专属产品（该账户品牌/公司下）"
                value={tagProductId}
                onChange={(e) => setTagProductId(e.target.value)}
                disabled={tagLoading || tagProducts.length === 0}
                sx={{ mt: 0.5 }}
              >
                {tagProducts.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              {tagLoading && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
                  正在加载产品候选...
                </Typography>
              )}
              {!tagLoading && tagProducts.length === 0 && !tagError && (
                <Typography sx={{ fontSize: 12, color: "warning.main", mt: 1 }}>
                  该品牌/公司下暂无产品，请先在「产品」页建档
                </Typography>
              )}
              <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
                设置后该账户仅在选择器选中此产品时以「专属」徽标出现；全公司其他
                产品不再共用此账户。解除专属可随时恢复共享。
              </Typography>
              {tagError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {tagError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTagRecord(null)} disabled={tagSubmitting} color="inherit">
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitTag}
            disabled={tagSubmitting || !tagProductId}
          >
            {tagSubmitting ? "提交中..." : "确认设置"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功提示 */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
